<template>
  <ui-collapse
    class="how-hear-about-us"
    :collapse-id="`collapse-how-did-you-hear-about-us`"
  >
    <template #ui-collapse-title>
      <translate tag="div"> How did you hear about Pitchup.com? </translate>
    </template>
    <template #ui-collapse-body>
      <div>
        <state-matches v-if="state" :state="state">
          <template #form>
            <form :class="{ error: isInvalid }" @submit.prevent="submitForm">
              <div v-if="isInvalid">
                <translate v-if="state.context.error === 'answerNotSelected'">
                  Please choose an option
                </translate>
                <span v-else>{{ state.context.error }}</span>
              </div>
              <base-radio
                v-for="answer in answers"
                :key="answer"
                name="howhear"
                :value="answer"
                @update:model-value="updateAnswer"
              >
                <translate>{{ answer }}</translate>
              </base-radio>
              <base-radio
                name="howhear"
                value="Somewhere else"
                @update:model-value="updateAnswer"
              >
                <base-input
                  maxlength="100"
                  name="howhear"
                  :model-value="state?.context.form.freeText"
                  :placeholder="$t('Somewhere else')"
                  @update:model-value="updateFreeText"
                />
              </base-radio>
              <base-submit-button class="button primary sml">
                <translate>Submit</translate>
                <template #submittingText>
                  <translate>Submitting</translate>
                </template>
              </base-submit-button>
            </form>
          </template>
          <template #submitting>
            <loading-spinner />
          </template>
          <template #submitted>
            <translate class="submitted">Thank you for your response</translate>
          </template>
        </state-matches>
      </div>
    </template>
  </ui-collapse>
</template>

<script setup lang="ts">
import { howDidYouHearAboutUsContainer } from '../inversify.config'
import { HowDidYouHearAboutUsMachineInterpreter } from '../machine'

const props = defineProps<{
  signature: string
}>()

let answers = [
  'Media coverage (e.g. news article)',
  'TV ad',
  'Friends and family',
  'Facebook/Instagram',
  'Search engine',
  'Used Pitchup before',
]

let machine: HowDidYouHearAboutUsMachineInterpreter | undefined
// Use shallowRef for XState - state is replaced on each transition
const state = shallowRef<HowDidYouHearAboutUsMachineInterpreter['state']>()

onMounted(() => {
  startMachine()
  randomizeAnswers()
})

function startMachine() {
  machine =
    howDidYouHearAboutUsContainer.get<HowDidYouHearAboutUsMachineInterpreter>(
      'HowDidYouHearAboutUsMachine',
    )
  machine
    .onTransition((newState) => {
      state.value = newState
    })
    .start()
}

const isInvalid = computed(() => {
  return state.value && state.value.matches('form.validation.invalid')
})

function randomizeAnswers() {
  answers = answers.sort(() => Math.random() - 0.5)
}

function updateAnswer(e: string) {
  machine?.send('UPDATE_ANSWER', { selected: e })
}

function updateFreeText(e: string) {
  machine?.send('UPDATE_FREE_TEXT', { freeText: e })
}

function submitForm() {
  machine?.send('SUBMIT', { signature: props.signature })
}
</script>

<style scoped>
.how-hear-about-us :deep(.ui-collapse-title),
.how-hear-about-us .submitted {
  font-weight: normal;
  font-size: var(--title);
}

.how-hear-about-us :deep(.ui-collapse-content label) {
  font-weight: normal;
  margin: 0 0 0.75em 0;
}

.how-hear-about-us :deep(.howhear-wrapper label),
.how-hear-about-us :deep(.howhear-wrapper input) {
  font-size: inherit;
}

.how-hear-about-us {
  margin: 0 0 2em;
  border: 1px solid #d6e6f1;
  border-radius: 0.5em;
  background-color: white;
}

.how-hear-about-us button {
  margin-top: 1em;
}

.how-hear-about-us :deep(.label-help) {
  display: none;
}

.how-hear-about-us :deep(.howhear-wrapper) {
  display: block;
  width: 100%;
}

.how-hear-about-us ::-webkit-input-placeholder {
  color: var(--colour-text-default);
}
.how-hear-about-us ::-moz-placeholder {
  color: var(--colour-text-default);
}
.how-hear-about-us :-ms-input-placeholder {
  color: var(--colour-text-default);
}
</style>
